import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/UserGameStats';
import type { UserProfile } from '@/types/UserProfile';
import { NotFoundError, ValidationError, handleSupabaseError } from './errors';

export async function getDisplayName(
  supabase: SupabaseClient<Database>,
): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const user = session?.user;
  if (!user) return null;
  return user.user_metadata?.full_name || user.user_metadata?.name || user.email || null;
}

export async function getUserProfile(
  supabase: SupabaseClient<Database>,
  userId: string,
): Promise<UserProfile | null> {
  if (!userId) throw new ValidationError('ユーザーIDが指定されていません');
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) handleSupabaseError(error);
  return data;
}

export async function createUserProfile(
  supabase: SupabaseClient<Database>,
  userId: string,
  userName?: string,
): Promise<UserProfile> {
  if (!userId) throw new ValidationError('ユーザーIDが指定されていません');

  const existing = await getUserProfile(supabase, userId);
  if (existing) return existing;

  const finalUserName = userName || (await getDisplayName(supabase)) || userId;
  const { data, error } = await supabase
    .from('user_profiles')
    .insert({ user_id: userId, user_name: finalUserName })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      const existingAfterConflict = await getUserProfile(supabase, userId);
      if (existingAfterConflict) return existingAfterConflict;
    }
    handleSupabaseError(error);
  }
  if (!data) throw new NotFoundError('プロフィールの作成に失敗しました');
  return data;
}

export async function updateUserName(
  supabase: SupabaseClient<Database>,
  userId: string,
  newUserName: string,
): Promise<UserProfile> {
  if (!userId) throw new ValidationError('ユーザーIDが指定されていません');
  const trimmed = newUserName.trim();
  if (!trimmed) throw new ValidationError('ユーザー名が指定されていません');

  const existing = await getUserProfile(supabase, userId);
  if (!existing) return createUserProfile(supabase, userId, trimmed);

  const { data, error } = await supabase
    .from('user_profiles')
    .update({ user_name: trimmed })
    .eq('user_id', userId)
    .select()
    .single();
  if (error) handleSupabaseError(error);
  if (!data) throw new NotFoundError('プロフィールの更新に失敗しました');
  return data;
}
